import React from 'react'
import Icon from './Icons'
import {
  getPrestationLabel,
  getComplexiteLabel,
  getVolumeLabel,
  getUrgenceLabel,
  getOptionLabel,
} from '../utils/pricingEngine'

export default function StepResults({ estimate, config, contact, onReset }) {
  if (!estimate) return null

  const hasOptions = config.options && config.options.length > 0

  return (
    <div className="step-enter space-y-6">

      {/* Bandeau de succès */}
      <div className="flex items-center gap-3 p-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Icon name="check" className="w-4 h-4 text-white" />
        </div>
        <p className="text-sm font-medium text-emerald-800">
          Adresse email confirmée — voici votre estimation.
        </p>
      </div>

      {/* Bloc prix principal */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-700 via-blue-800 to-slate-800 p-8 text-white shadow-xl">
        {/* Cercles décoratifs */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -bottom-12 -left-8 w-52 h-52 rounded-full bg-white/5" />

        <div className="relative z-10">
          <p className="text-blue-200 text-sm font-medium mb-1 uppercase tracking-widest">
            Estimation indicative
          </p>
          <div className="flex items-baseline gap-3 flex-wrap my-3">
            <span className="text-4xl sm:text-5xl font-bold tracking-tight">
              {estimate.formatted.min}
            </span>
            <span className="text-2xl text-blue-300 font-light">→</span>
            <span className="text-4xl sm:text-5xl font-bold tracking-tight">
              {estimate.formatted.max}
            </span>
          </div>
          <p className="text-blue-200 text-sm">
            Hors taxes · Estimation non contractuelle
          </p>
        </div>
      </div>

      {/* Mention légale */}
      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-50 border border-amber-200">
        <Icon name="info" className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          <span className="font-semibold">Estimation indicative, devis précis sur demande.</span>{' '}
          Cette fourchette est calculée à partir de projets similaires et peut varier selon les
          spécifications détaillées, les contraintes techniques et les conditions de réalisation.
        </p>
      </div>

      {/* Résumé de la configuration */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Récapitulatif de votre projet</h3>
        </div>
        <div className="divide-y divide-slate-100">
          <SummaryRow label="Prestation"  value={getPrestationLabel(config.prestation)} />
          <SummaryRow label="Complexité"  value={getComplexiteLabel(config.complexite)} />
          <SummaryRow label="Volume"      value={getVolumeLabel(config.volume)} />
          <SummaryRow label="Délai"       value={getUrgenceLabel(config.urgence)} />
          {hasOptions && (
            <SummaryRow
              label="Options"
              value={config.options.map(getOptionLabel).join(', ')}
            />
          )}
          <SummaryRow label="Contact"     value={`${contact.prenom} ${contact.nom} – ${contact.societe}`} />
          <SummaryRow label="Email"       value={contact.email} />
        </div>
      </div>

      {/* CTA */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <a
          href="mailto:contact@e2i-solutions.com?subject=Demande de devis – simulation en ligne"
          className="btn-primary text-center text-sm"
        >
          <Icon name="mail" className="w-4 h-4" />
          Demander un devis précis
        </a>
        <a
          href="tel:+33XXXXXXXXX"
          className="btn-secondary text-center text-sm"
        >
          <Icon name="phone" className="w-4 h-4" />
          Nous appeler
        </a>
      </div>

      {/* Recommencer */}
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={onReset}
          className="text-sm text-slate-400 hover:text-slate-600 transition-colors duration-150 inline-flex items-center gap-1.5"
        >
          <Icon name="refresh" className="w-3.5 h-3.5" />
          Simuler un autre projet
        </button>
      </div>
    </div>
  )
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex gap-4 px-5 py-3">
      <span className="text-xs font-medium text-slate-500 w-24 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-800 leading-relaxed">{value}</span>
    </div>
  )
}
